import React from 'react';
import type { TargetAudienceFilter } from '../types/Campaign';

interface GenericFilterTabProps {
  filter: TargetAudienceFilter;
  onChange: (filter: TargetAudienceFilter) => void;
  disabled?: boolean;
}

// Dropdown options - in the future, these could come from a config
const REGIONS = [
  { value: '', label: 'Select Region' },
  { value: 'APAC', label: 'Asia Pacific' },
  { value: 'EMEA', label: 'Europe, Middle East & Africa' },
  { value: 'NA', label: 'North America' },
  { value: 'LATAM', label: 'Latin America' },
];

const INDUSTRIES = [
  { value: '', label: 'Select Industry' },
  { value: 'Software', label: 'Software & Technology' },
  { value: 'Finance', label: 'Financial Services' },
  { value: 'Healthcare', label: 'Healthcare & Life Sciences' },
  { value: 'Manufacturing', label: 'Manufacturing' },
  { value: 'Retail', label: 'Retail & E-commerce' },
  { value: 'Education', label: 'Education' },
  { value: 'Real Estate', label: 'Real Estate' },
  { value: 'Consulting', label: 'Consulting Services' },
];

const POSITIONS = [
  { value: '', label: 'Select Position' },
  { value: 'Marketing Manager', label: 'Marketing Manager' },
  { value: 'Sales Director', label: 'Sales Director' },
  { value: 'CEO', label: 'CEO / Founder' },
  { value: 'CTO', label: 'CTO / Technical Lead' },
  { value: 'Business Development', label: 'Business Development' },
  { value: 'Product Manager', label: 'Product Manager' },
  { value: 'Operations Manager', label: 'Operations Manager' },
  { value: 'HR Director', label: 'HR Director' },
];

const GenericFilterTab: React.FC<GenericFilterTabProps> = ({ filter, onChange, disabled = false }) => {
  const handleFieldChange = (field: keyof TargetAudienceFilter, value: string) => {
    onChange({
      ...filter,
      [field]: value
    });
  };

  const isComplete = filter.region && filter.industry && filter.position;

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">Generic Lead Filtering</h3>
            <div className="mt-1 text-sm text-blue-700">
              <p>Select criteria to filter leads from our database. All three fields are required.</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6">
        {/* Region Selection */}
        <div>
          <label htmlFor="region" className="block text-sm font-medium text-gray-700 mb-2">
            Region *
          </label>
          <select
            id="region"
            value={filter.region || ''}
            onChange={(e) => handleFieldChange('region', e.target.value)}
            disabled={disabled}
            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
          >
            {REGIONS.map((region) => (
              <option key={region.value} value={region.value}>
                {region.label}
              </option>
            ))}
          </select>
        </div>

        {/* Industry Selection */}
        <div>
          <label htmlFor="industry" className="block text-sm font-medium text-gray-700 mb-2">
            Industry *
          </label>
          <select
            id="industry"
            value={filter.industry || ''}
            onChange={(e) => handleFieldChange('industry', e.target.value)}
            disabled={disabled}
            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
          >
            {INDUSTRIES.map((industry) => (
              <option key={industry.value} value={industry.value}>
                {industry.label}
              </option>
            ))}
          </select>
        </div>

        {/* Position Selection */}
        <div>
          <label htmlFor="position" className="block text-sm font-medium text-gray-700 mb-2">
            Position *
          </label>
          <select
            id="position"
            value={filter.position || ''}
            onChange={(e) => handleFieldChange('position', e.target.value)}
            disabled={disabled}
            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
          >
            {POSITIONS.map((position) => (
              <option key={position.value} value={position.value}>
                {position.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Completion Status */}
      {isComplete && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-green-800">
                Generic filter configured: {filter.region}, {filter.industry}, {filter.position}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GenericFilterTab;
